import {Injectable} from "@angular/core";
import {Geolocation, Position, PositionOptions} from "@capacitor/geolocation";
import {LocationInfo} from "~/app/core/model/location-info";
import * as Log from "~/app/core/log";
import {Capacitor} from "@capacitor/core";
import {CallbackID} from "@capacitor/geolocation/dist/esm/definitions";

export class LocationTracker {
    watch?: CallbackID;
    callback: (loc: LocationInfo) => void;
}

@Injectable()
export class LocationService {
    private web: boolean = false;

    // private _options: geolocation.Options = {desiredAccuracy: Accuracy.high, maximumAge: 15000, timeout: 15000, updateTime: 15000, updateDistance: 50, iosAllowsBackgroundLocationUpdates: false };
    private _options: PositionOptions = {enableHighAccuracy: true, maximumAge: 15000, timeout: 15000};
    private _trackers: Array<LocationTracker> = new Array();
    private updating: boolean = false;
    private coordinates: LocationInfo = null;
    private expireCoordinates: number = null;

    constructor(
    ) {
        // nothing
        this.web = Capacitor.getPlatform() === "web";
    }

    isAllowLocation(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            Geolocation.checkPermissions().then((result) => {
                if (result.location === 'granted') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            }, (error) => resolve(false));
        });
    }

    requestLocationPermission(): Promise<boolean> {
        if (this.web) {
            return new Promise((resolve, reject) => {
                Geolocation.checkPermissions().then((result) => {
                    if (result.location === 'granted') {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                }, (error) => resolve(false));
            });
        } else {
            return new Promise((resolve, reject) => {
                Geolocation.requestPermissions({
                    permissions: ['location']
                }).then((permissionStatus) => {
                    if (permissionStatus.location !== 'denied') {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                }).catch(error => {
                    resolve(false);
                });
            });
        }
    }

    getLocationInfo(): Promise<LocationInfo> {
        return this.updateLocation();
    }

    startWatchingLocationInfo(callback: (loc: LocationInfo) => void): LocationTracker {
        try {
            Log.trace(Log.Tag.LocationService, "start watching");
            const tracker: LocationTracker = {
                callback: callback
            };
            this._trackers.push(tracker);
            this.resumeLocation();
            return tracker;
        } catch (error) {
            // if an error happens, then it might just mean that GPS isn't allowed, so return null
            return null;
        }
    }

    stopWatchingLocationInfo(tracker: LocationTracker) {
        if (tracker) {
            Log.trace(Log.Tag.LocationService, "stop watching");
            if (tracker.watch) {
                Geolocation.clearWatch({
                    id: tracker.watch
                });
            }
            this._trackers = this._trackers.filter((item) => item !== tracker);
        }
    }

    pauseLocation(): void {
        this._trackers.forEach((tracker, index) => {
            if (tracker.watch) {
                Log.debug(Log.Tag.LocationService, "PAUSE WATCHING TRACKER: " + index);
                Geolocation.clearWatch({ id: tracker.watch });
            }
            tracker.watch = null;
        });
    }

    resumeLocation(): void {

        this._trackers.forEach((tracker, index) => {
            if (!tracker.watch) {
                Log.debug(Log.Tag.LocationService, "START WATCHING TRACKER: " + index);
                try {
                    Geolocation.watchPosition(this._options, (position: Position | null, err?: any) => {
                        if (position) {
                            const loc = {
                                latitude: position.coords.latitude,
                                longitude: position.coords.longitude
                            };
                            this.setCoordinates(loc);
                            tracker.callback(loc);
                        }
                    }).then((watch: CallbackID) => {
                        tracker.watch = watch;
                    }, (error: Error) => {
                        tracker.callback(null);
                    });
                } catch (error) {
                    tracker.callback(null);
                }
            }
        });
    }

    private updateLocation(): Promise<LocationInfo> {
        return new Promise((resolve, reject) => {
            let resolved = false;
            let storedLoc = this.getCoordinates();
            if (storedLoc) {
                resolve(storedLoc);
                resolved = true;
            }
            if (!this.updating || !storedLoc) {
                this.updating = true;
                Log.trace(Log.Tag.LocationService, "request update location");
                console.log("getCurrentPosition...");
                Geolocation.getCurrentPosition(this._options).then(position => {
                  console.log("position:");
                  console.log(position);
                    this.updating = false;
                    if (!storedLoc || storedLoc.longitude !== position.coords.longitude || storedLoc.latitude !== position.coords.latitude) {
                        Log.trace(Log.Tag.LocationService, "newlong, newlat: " + position.coords.longitude + ", " + position.coords.latitude);
                        storedLoc = {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude
                        };
                        this.setCoordinates(storedLoc);
                    }
                    if (!resolved) {
                        resolve(storedLoc);
                    }
                }, (error) => {
                  console.log("getCurrentPosition failed.")
                  console.log(error);
                    this.updating = false;
                    this.setCoordinates(null);
                    if (!resolved) {
                        reject(error);
                    }
                });
            }
        });
    }

    private setCoordinates(info: LocationInfo) {
        this.coordinates = info;
        this.expireCoordinates = new Date().getTime() + 60000;
    }

    private getCoordinates(): LocationInfo {
        if (this.coordinates && this.expireCoordinates < new Date().getTime()) {
            this.coordinates = null;
        }
        return this.coordinates;
    }
}
